import { createHash, randomUUID } from "node:crypto";

export const PRODUCTION_CAPABILITY_STATES = [
  "AVAILABLE",
  "REDIRECT_ONLY",
  "REQUEST_ONLY",
  "UNAVAILABLE",
  "TEMPORARILY_DISABLED"
] as const;

export type ProductionCapabilityState = (typeof PRODUCTION_CAPABILITY_STATES)[number];

export type RuntimeMode = "local" | "test" | "sandbox" | "production";
export type EntitlementRequirement = "FREE" | "INSIDER" | "CREATOR" | "LIFETIME_CREATOR" | "ADMIN_ONLY";
export type LegalStatus = "APPROVED" | "PENDING_LEGAL" | "PROHIBITED" | "PROVIDER_NOT_CONTRACTED" | "TEMPORARILY_BLOCKED";
export type ProviderMode =
  | "redirect"
  | "embedded"
  | "merchant"
  | "request"
  | "display"
  | "directions_handoff"
  | "discovery"
  | "access"
  | "booking"
  | "subscription"
  | "digital_purchase"
  | "restore"
  | "hosted_onboarding"
  | "native_broadcast"
  | "playback"
  | "chat"
  | "sign_in"
  | "transaction_request"
  | "wallet_link"
  | "payout"
  | "settlement"
  | "reconciliation"
  | "text"
  | "stt"
  | "tts"
  | "translation"
  | "glossary"
  | "outbound_email"
  | "inbound_webhook";

export type MerchantOfRecord = "PORTABILITY" | "PROVIDER" | "APP_STORE" | "GOOGLE_PLAY" | "NOT_APPLICABLE";
export type RefundSourceOfTruth = "PORTABILITY" | "PROVIDER" | "APP_STORE" | "GOOGLE_PLAY" | "NOT_APPLICABLE";
export const COMMISSION_TYPES = [
  "NONE",
  "AFFILIATE",
  "PROVIDER_COMMISSION",
  "BOOKING_PURCHASE_OVERRIDE",
  "SUBSCRIPTION_REFERRAL",
  "MANUAL_REVIEW"
] as const;
export type CommissionType = (typeof COMMISSION_TYPES)[number];

export interface CapabilityKey {
  country: string;
  region: string | null;
  category: string;
  provider: string;
  mode: ProviderMode;
  currency: string;
  language: string;
  entitlement: EntitlementRequirement;
  legalStatus: LegalStatus;
}

export interface CapabilityRecord extends CapabilityKey {
  state: ProductionCapabilityState;
  supportedCurrencies: readonly string[];
  supportedLanguages: readonly string[];
  sponsorCommissionSharingPermitted: boolean;
  commissionType: CommissionType;
  sourceOfTruth: "provider" | "portability" | "store" | "manual_review";
  merchantOfRecord: MerchantOfRecord;
  refundSourceOfTruth: RefundSourceOfTruth;
  attributionRequired: boolean;
  cachePolicy: "none" | "identifier_only" | "contract_snapshot" | "portability_owned";
  credentialVersion: string | null;
  legalApprovalRef: string | null;
  healthCheckedAt: string | null;
  effectiveFrom: string;
  expiresAt: string | null;
  sandbox: false;
}

export interface AuditActor {
  type: "system" | "user" | "admin" | "provider" | "worker";
  id: string;
}

export interface AuditEventInput {
  type: string;
  actor: AuditActor;
  occurredAt?: string;
  subject: {
    type: string;
    id: string;
  };
  reasonCode: string;
  metadata?: Readonly<Record<string, unknown>>;
  requestId?: string;
}

export interface AuditEvent extends Required<Omit<AuditEventInput, "metadata" | "occurredAt" | "requestId">> {
  id: string;
  occurredAt: string;
  requestId: string | null;
  metadata: Readonly<Record<string, unknown>>;
  schemaVersion: 1;
  contentHash: string;
}

export interface RuntimeEnvironmentDefinition {
  name: RuntimeMode;
  production: boolean;
  providerFixturePolicy: "allowed" | "forbidden";
  clientPublicKeys: readonly string[];
  requiredServerSecretKeys: readonly string[];
}

export interface LoadedRuntimeConfig {
  environment: RuntimeMode;
  publicConfig: Readonly<Record<string, string>>;
  getSecret(key: string): string;
}

export const authority = Object.freeze({
  applicationId: "com.portability.app",
  applicationName: "Portability",
  financialMutationAuthority: "server",
  entitlementMutationAuthority: "server"
} as const);

export const authMethods = ["apple", "google", "email_magic_link", "browse_signed_out"] as const;
export type AuthMethod = (typeof authMethods)[number];

export const supportedLocales = [
  "en",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "ja",
  "ko",
  "zh-Hans",
  "zh-Hant"
] as const;

function normalizeCode(value: string, field: string) {
  if (!/^[A-Z0-9_-]{2,32}$/.test(value)) throw new Error(`Invalid ${field}: ${value}`);
  return value;
}

function requireNonBlank(value: string, field: string) {
  if (value.trim().length === 0) throw new Error(`${field} must not be blank`);
}

function parseIsoTimestamp(value: string, field: string) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) {
    throw new Error(`${field} must be a canonical ISO timestamp`);
  }
  return parsed;
}

export function capabilityRecordKey(record: CapabilityKey) {
  return [
    normalizeCode(record.country, "country"),
    record.region === null ? "*" : normalizeCode(record.region, "region"),
    record.category,
    record.provider,
    record.mode,
    normalizeCode(record.currency, "currency"),
    record.language,
    record.entitlement,
    record.legalStatus
  ].join(":");
}

export function assertProductionCapabilityRecord(record: CapabilityRecord) {
  if (!PRODUCTION_CAPABILITY_STATES.includes(record.state)) {
    throw new Error(`Unsupported production capability state: ${String(record.state)}`);
  }
  if (record.sandbox !== false) throw new Error("Sandbox capability records cannot be production records");
  if (!(COMMISSION_TYPES as readonly string[]).includes(record.commissionType)) {
    throw new Error(`Unsupported commission type: ${String(record.commissionType)}`);
  }
  if (!/^[A-Z]{2}$/.test(record.country)) throw new Error(`Capability country must be ISO alpha-2: ${record.country}`);
  if (!/^[A-Z]{3}$/.test(record.currency)) throw new Error(`Capability currency must be ISO alpha-3: ${record.currency}`);
  requireNonBlank(record.category, "Capability category");
  requireNonBlank(record.provider, "Capability provider");
  requireNonBlank(record.language, "Capability language");
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(record.category)) {
    throw new Error(`Capability category must be a canonical slug: ${record.category}`);
  }
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(record.provider)) {
    throw new Error(`Capability provider must be a canonical slug: ${record.provider}`);
  }
  if (!/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(record.language)) {
    throw new Error(`Capability language must be a BCP-47-style tag: ${record.language}`);
  }
  if (!record.supportedCurrencies.every((currency) => /^[A-Z]{3}$/.test(currency))) {
    throw new Error("Supported capability currencies must use ISO alpha-3 codes");
  }
  if (!record.supportedLanguages.every((language) => /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(language))) {
    throw new Error("Supported capability languages must use BCP-47-style tags");
  }
  if (!record.supportedCurrencies.includes(record.currency)) throw new Error(`Currency ${record.currency} is not supported by record`);
  if (!record.supportedLanguages.includes(record.language)) throw new Error(`Language ${record.language} is not supported by record`);
  const actionable = record.state === "AVAILABLE" || record.state === "REDIRECT_ONLY" || record.state === "REQUEST_ONLY";
  if (actionable && record.legalStatus !== "APPROVED") {
    throw new Error(`${record.state} capability records require legal approval`);
  }
  if (actionable && (
    record.credentialVersion === null || record.credentialVersion.trim().length === 0 ||
    record.legalApprovalRef === null || record.legalApprovalRef.trim().length === 0 ||
    record.healthCheckedAt === null
  )) {
    throw new Error(`${record.state} capability records require credential, legal approval, and health-check evidence`);
  }
  if (actionable) parseIsoTimestamp(record.healthCheckedAt!, "Capability health check");
  const effectiveAt = parseIsoTimestamp(record.effectiveFrom, "Capability effective time");
  const expiresAt = record.expiresAt === null ? null : parseIsoTimestamp(record.expiresAt, "Capability expiry time");
  if (expiresAt !== null && expiresAt <= effectiveAt) {
    throw new Error("Capability expiry must be later than its effective time");
  }
  return record;
}

function deepFreeze<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  for (const key of Reflect.ownKeys(value)) {
    deepFreeze((value as Record<PropertyKey, unknown>)[key]);
  }
  return Object.freeze(value);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

export function createAuditEvent(input: AuditEventInput): AuditEvent {
  if (input.reasonCode.trim().length === 0) throw new Error("Audit events require a reason code");
  const eventWithoutHash = {
    id: randomUUID(),
    schemaVersion: 1 as const,
    type: input.type,
    actor: input.actor,
    occurredAt: input.occurredAt ?? new Date().toISOString(),
    subject: input.subject,
    reasonCode: input.reasonCode,
    requestId: input.requestId ?? null,
    metadata: input.metadata ?? {}
  };
  const contentHash = createHash("sha256").update(stableJson(eventWithoutHash)).digest("hex");
  return deepFreeze({ ...eventWithoutHash, contentHash });
}

export function loadRuntimeConfig(options: {
  environment: RuntimeMode;
  definitions: readonly RuntimeEnvironmentDefinition[];
  env: Readonly<Record<string, string | undefined>>;
  publicPrefix?: string;
}): LoadedRuntimeConfig {
  const definition = options.definitions.find(({ name }) => name === options.environment);
  if (!definition) throw new Error(`Unknown runtime environment: ${options.environment}`);
  const publicPrefix = options.publicPrefix ?? "PORTABILITY_PUBLIC_";
  const publicConfig: Record<string, string> = {};
  for (const key of definition.clientPublicKeys) {
    if (!key.startsWith(publicPrefix)) throw new Error(`Client config key ${key} must use ${publicPrefix}`);
    const value = options.env[key];
    if (typeof value === "string") publicConfig[key] = value;
  }
  const requiredSecrets = new Set(definition.requiredServerSecretKeys);
  for (const key of requiredSecrets) {
    if (key.startsWith(publicPrefix)) throw new Error(`Server secret key ${key} must not use ${publicPrefix}`);
    if (definition.production && !options.env[key]) throw new Error(`Missing required production secret: ${key}`);
  }
  return Object.freeze({
    environment: definition.name,
    publicConfig: Object.freeze(publicConfig),
    getSecret(key: string) {
      if (!requiredSecrets.has(key)) throw new Error(`Secret key is not declared for ${definition.name}: ${key}`);
      const value = options.env[key];
      if (!value) throw new Error(`Secret ${key} is not loaded`);
      return value;
    }
  });
}
